import { createPortal } from 'react-dom';
import { INSURANCE } from './mockData';
import { SOURCE_LABEL } from './types';
import { daysUntil, fmtDate, fmtStamp, money } from './utils';

const fx = INSURANCE;

/**
 * The eligibility report (handoff §4.13). Same facts as the screen, a
 * different rendering. Portaled to <body>, hidden on screen, revealed in
 * print; `@media print` hides the app so paper carries no sidebar, nav or
 * buttons. Built to be identical to the on-screen numbers — never a second
 * data source.
 */
export function PrintReport() {
  const p = fx.patient;
  const sunlife = fx.plans[0];
  const resetDays = sunlife ? daysUntil(sunlife.resets, fx.today) : 0;
  const sun = fx.benefits.find((b) => b.planId === 'sunlife');
  const e = fx.visitEstimate;

  const report = (
    <div className="ins-print-portal">
      <div className="ins-print">
        {/* Header */}
        <header className="ins-print-head">
          <div>
            <div className="ins-print-practice">{fx.report.practice} · {fx.report.location}</div>
            <h1>Eligibility report</h1>
          </div>
          <div className="ins-print-meta">
            <div>Prepared by {fx.report.preparedBy}</div>
            <div>Facts as of {fmtStamp(fx.verification.lastAt)}</div>
            <div>Report {fx.report.id} · snapshot {fx.report.snapshotId}</div>
          </div>
        </header>

        {/* Patient and plans */}
        <section className="ins-print-sec">
          <h2>Patient and plans</h2>
          <p><strong>{p.name}</strong> · {p.age} yrs · patient since {p.since} · {p.phone}</p>
          {fx.plans.map((pl) => (
            <p key={pl.id}>
              {pl.order === 'primary' ? 'Primary' : 'Secondary'} · {pl.carrier} {pl.plan} ({pl.type}, {pl.network === 'in' ? 'in-network' : 'out-of-network'}) ·
              Member {pl.memberId} · Group {pl.group} · Subscriber {pl.subscriber} ({pl.relationship}) · Payer ID {pl.payerId}
            </p>
          ))}
          <p>{fx.coordinationLine}</p>
          <p><em>Billing preference:</em> {fx.billingPreference.text} (set by {fx.billingPreference.by}, Jul 30, 2026)</p>
        </section>

        {/* Benefits */}
        <section className="ins-print-sec">
          <h2>Benefits left this year</h2>
          <table className="ins-print-table">
            <thead><tr><th>Plan</th><th>Annual max</th><th>Used</th><th>Left</th><th>Resets</th></tr></thead>
            <tbody>
              {fx.benefits.map((b) => (
                <tr key={b.planId}>
                  <td>{b.planId === 'sunlife' ? 'Sunlife' : 'MetLife'}</td>
                  <td>{money(b.annualMax)}</td>
                  <td>{money(b.used)}</td>
                  <td>{money(b.remaining)}</td>
                  <td>{fmtDate(b.resets)} · {daysUntil(b.resets, fx.today)} days</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sun?.notCounted ? <p className="ins-print-note">{sun.notCounted.text}</p> : null}
        </section>

        {/* Shared allowances */}
        <section className="ins-print-sec">
          <h2>Shared allowances</h2>
          <table className="ins-print-table">
            <thead><tr><th>Allowance</th><th>Codes that draw on it</th><th>Limit</th><th>Used</th><th>Left</th></tr></thead>
            <tbody>
              {fx.allowances.filter((a) => a.allowance !== 'Night guards').map((a, i) => (
                <tr key={i}><td>{a.allowance}</td><td>{a.drawsOn}</td><td>{a.limit}</td><td>{a.used}</td><td>{a.left}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="ins-print-note">Using any code in an allowance uses the allowance. “Left” counts what this office used and what Sunlife reports as used; care at another office appears only if the insurer reports it.</p>
        </section>

        {/* Services checked — per code (PrintView.dc.html) */}
        <section className="ins-print-sec">
          <h2>Services checked</h2>
          <table className="ins-print-table">
            <thead><tr><th>Code</th><th>Service</th><th>Plan pays</th><th>Frequency</th><th>Last done</th><th>Next covered</th><th>Notes</th></tr></thead>
            <tbody>
              {fx.reportServices.map((r) => (
                <tr key={r.code}>
                  <td>{r.code}</td>
                  <td>{r.service}</td>
                  <td>{r.planPays}</td>
                  <td>{r.frequency}</td>
                  <td>{r.lastDone}</td>
                  <td>{r.nextCovered}</td>
                  <td>{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Plan rules */}
        <section className="ins-print-sec">
          <h2>Plan rules</h2>
          <table className="ins-print-table">
            <thead><tr><th>Rule</th><th>What it means</th><th>How ELVA knows</th></tr></thead>
            <tbody>
              {fx.planRules.map((r) => (
                <tr key={r.id}>
                  <td>{r.rule}</td>
                  <td>{r.meaning}</td>
                  <td>{r.notYetKnown ? 'Not yet known' : r.howKnown}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Estimate */}
        <section className="ins-print-sec">
          <h2>Estimate for the Oct 14 visit</h2>
          <table className="ins-print-table">
            <thead><tr><th>Procedure</th><th>Fee</th><th>Allowed</th><th>Insurance</th><th>Patient</th></tr></thead>
            <tbody>
              {e.lines.map((l) => (
                <tr key={l.code}>
                  <td>{l.code} {l.label}{l.note ? ` — ${l.note}` : ''}</td>
                  <td>{money(l.fee)}</td><td>{money(l.allowed)}</td><td>{money(l.insurance)}</td><td>{money(l.patient)}</td>
                </tr>
              ))}
              <tr className="ins-print-total">
                <td>Total</td><td>{money(e.totals.fee)}</td><td>{money(e.totals.allowed)}</td><td>{money(e.totals.insurance)}</td><td>{money(e.totals.patient)}</td>
              </tr>
            </tbody>
          </table>
          <p className="ins-print-note">Contract write-off {money(e.totals.writeOff)}. {money(sun?.remaining ?? 0)} of the Sunlife maximum before the visit; {money(e.maxAfterVisit)} after it. An estimate, not a guarantee of payment.</p>
        </section>

        {/* How this was verified */}
        <section className="ins-print-sec">
          <h2>How this was verified</h2>
          <table className="ins-print-table">
            <thead><tr><th>Source</th><th>Last checked</th><th>Citation</th></tr></thead>
            <tbody>
              {(fx.sources.sunlife ?? []).map((s) => (
                <tr key={s.kind}>
                  <td>{SOURCE_LABEL[s.kind]}</td>
                  <td>{s.lastChecked}</td>
                  <td>{s.citation ?? '—'}{s.weak ? ' · verbal, not independently documented' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Not yet known */}
        <section className="ins-print-sec">
          <h2>Not yet known</h2>
          <ul>
            <li>How MetLife pays as secondary — AI Call booked Oct 12; today’s estimate assumes MetLife adds $0.</li>
            <li>Night guard (D9944) pre-auth — in review, decision expected Oct 20; the insurance share is not yet decided.</li>
          </ul>
        </section>

        <footer className="ins-print-foot">
          Resets in {resetDays} days. This eligibility report reflects the facts ELVA verified as of {fmtStamp(fx.verification.lastAt)} and is not a guarantee of payment. {fx.report.practice} · Report {fx.report.id}.
        </footer>
      </div>
    </div>
  );

  return createPortal(report, document.body);
}
